import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, Sparkles, Search, Target, CheckCircle2, ArrowRight, Briefcase, FileEdit, Heart, Users, Mail } from 'lucide-react';
import AdRenderer from '@/components/AdRenderer';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileEdit className="w-6 h-6" />,
      title: "AI Resume Builder",
      description: "Create professional, ATS-friendly resumes with AI-powered suggestions"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Instant Modernize",
      description: "Upload your old resume and transform it into a modern, polished format"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Job Tailoring",
      description: "Optimize your resume for specific job postings with ATS match scoring"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Cover Letter Generator",
      description: "Generate compelling, personalized cover letters in seconds"
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: "Job Search",
      description: "Search and save jobs from across the internet in one place"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Application Tracker",
      description: "Track your applications and stay organized throughout your job search"
    }
  ];

  const benefits = [
    "ATS-Friendly Templates",
    "Keyword Optimization",
    "Multiple Resume Versions",
    "Professional Guidance",
    "Easy to Use Interface",
    "100% Free to Start"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50">
      {/* Navigation */}
      <nav className="px-4 sm:px-6 py-2 bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 overflow-visible">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_role-expiry-dash/artifacts/swo6wssk_career_support_80px_transparent_EXACT.png" 
              alt="Career Support" 
              className="h-28 sm:h-36 w-auto -my-8"
            />
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="hover:bg-sky-50 text-gray-700"
              data-testid="nav-home-btn"
            >
              Home
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/about')}
              className="hover:bg-sky-50 text-gray-700"
              data-testid="nav-about-btn"
            >
              About
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/contact')}
              className="hover:bg-sky-50 text-gray-700"
              data-testid="nav-contact-btn"
            >
              Contact
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/services')}
              className="hover:bg-sky-50 text-gray-700"
              data-testid="nav-services-btn"
            >
              Services
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/workshops')}
              className="hover:bg-sky-50 text-gray-700"
              data-testid="nav-workshops-btn"
            >
              Workshops
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn"
              className="hover:bg-sky-50"
            >
              Log In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              data-testid="nav-register-btn"
              className="bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-lg shadow-sky-500/30"
            >
              Get Started Free
            </Button>
          </div>
          
          {/* Mobile Navigation - Stacked buttons */}
          <div className="flex md:hidden flex-col space-y-2">
            <div className="flex space-x-2 flex-wrap gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/')}
                className="hover:bg-sky-50 text-sm px-2"
              >
                Home
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/about')}
                className="hover:bg-sky-50 text-sm px-2"
              >
                About
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/contact')}
                className="hover:bg-sky-50 text-sm px-2"
              >
                Contact
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate('/services')}
                className="hover:bg-sky-50 text-sm px-2"
              >
                Services
              </Button>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/register')}
              data-testid="nav-register-btn-mobile"
              className="bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-md text-sm px-4"
            >
              Get Started Free
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate('/login')}
              data-testid="nav-login-btn-mobile"
              className="hover:bg-sky-50 text-sm px-4"
            >
              Log In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-12 md:py-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-orange-100 rounded-full text-orange-700 text-sm font-medium">
            Your Career Success Partner
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Build Your Dream Resume.
            <br />
            <span className="bg-gradient-to-r from-sky-600 to-orange-600 bg-clip-text text-transparent">
              Land Your Dream Job.
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Create professional, ATS-friendly resumes, generate tailored cover letters, and search for jobs, all in one powerful platform designed for jobseekers.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              data-testid="hero-get-started-btn"
              className="text-lg px-8 py-6 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-xl shadow-sky-500/30"
            >
              Start Building Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/register')}
              data-testid="hero-upload-resume-btn"
              className="text-lg px-8 py-6 border-2 border-sky-500 text-sky-600 hover:bg-sky-50"
            >
              Upload Existing Resume
            </Button>
          </div>
          
          {/* Professional Services CTA */}
          <div className="mb-12 p-4 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl border border-purple-100 max-w-2xl mx-auto">
            <p className="text-gray-700 mb-3">
              <span className="font-semibold text-purple-700">Need Expert Guidance?</span> Work with our professional career coaches
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/services')}
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                data-testid="hero-services-btn"
              >
                View Coaching Services
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/workshops')}
                className="border-orange-300 text-orange-700 hover:bg-orange-100"
                data-testid="hero-workshops-btn"
              >
                Explore Workshops
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Development Features Section */}
      <section className="px-6 py-16 bg-gradient-to-br from-sky-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Powerful Tools to Grow Your Career</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Your career is more than a resume. CareerSupport gives you everything you need to build, plan, and advance your professional journey.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Career Planning */}
            <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Career Planning</h3>
              <p className="text-gray-600 leading-relaxed">
                A guided career planning tool that helps you set personalized goals, create action steps, and track your progress toward long-term success.
              </p>
            </div>

            {/* ATS Resume Grader */}
            <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">ATS Resume Grader</h3>
              <p className="text-gray-600 leading-relaxed">
                Instantly scan your resume and see how well it performs against Applicant Tracking Systems. Get clear scoring and actionable feedback.
              </p>
            </div>

            {/* Career Assessments */}
            <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                <Sparkles className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Career Assessments</h3>
              <p className="text-gray-600 leading-relaxed">
                Identify your strengths, work style, interests, and ideal pathways with research-backed assessments used by workforce professionals.
              </p>
            </div>

            {/* Career Tools Library */}
            <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Career Tools Library</h3>
              <p className="text-gray-600 leading-relaxed">
                A complete library of templates, guides, best practices, and resources to help you navigate every stage of your career journey.
              </p>
            </div>

            {/* Interview Prep */}
            <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                <Briefcase className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Interview Prep</h3>
              <p className="text-gray-600 leading-relaxed">
                Master your interviews with AI-generated questions, tailored answers, confidence prompts, and professional communication guidance.
              </p>
            </div>

            {/* Salary Analyzer */}
            <div className="p-8 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                <Search className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Salary Analyzer</h3>
              <p className="text-gray-600 leading-relaxed">
                Compare salaries by job title and location so you know your worth and can negotiate with confidence.
              </p>
            </div>
          </div>
          
          {/* Optional Tagline */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 font-medium">
              Everything you need to develop, grow, and advance in your career, all in one place.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Everything You Need to Succeed</h2>
            <p className="text-lg text-gray-600">Powerful tools to help you stand out in your job search</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-200 card-hover"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center text-white mb-4 shadow-lg shadow-sky-500/30">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Search Preview Section */}
      <section className="px-6 py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Integrated Job Search</h2>
            <p className="text-lg text-gray-600">Find, save, and apply to jobs all in one place</p>
          </div>
          {/* Job Search Mockup */}
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 text-white">
              <div className="flex items-center space-x-2">
                <Search className="w-5 h-5" />
                <span className="font-semibold">Job Search</span>
              </div>
            </div>
            <div className="p-8">
              {/* Top Row - Search Inputs */}
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                {/* Box 1 - Job Title/Keyword Field */}
                <div className="sm:col-span-1">
                  <div className="relative">
                    <input
                      type="text"
                      value="Customer Service Representative"
                      readOnly
                      className="w-full h-12 px-4 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:outline-none cursor-default"
                      placeholder="Job Title or Keyword"
                    />
                    <div className="absolute top-[-20px] left-0 text-xs text-gray-500">Job Title or Keyword</div>
                  </div>
                </div>
                
                {/* Box 2 - Location Field */}
                <div className="sm:col-span-1">
                  <div className="relative">
                    <input
                      type="text"
                      value="Atlanta, GA, United States"
                      readOnly
                      className="w-full h-12 px-4 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm focus:outline-none cursor-default"
                      placeholder="Location"
                    />
                    <div className="absolute top-[-20px] left-0 text-xs text-gray-500">Location</div>
                  </div>
                </div>
                
                {/* Box 3 - Search Button */}
                <div className="sm:col-span-1">
                  <button
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg hover:from-green-600 hover:to-green-700 transition-colors cursor-default flex items-center justify-center space-x-2"
                    disabled
                  >
                    <Search className="w-4 h-4" />
                    <span>Search Jobs</span>
                  </button>
                </div>
              </div>

              {/* Middle Section - Popular Searches */}
              <div className="mb-8 pb-8 border-b border-gray-200">
                <div className="flex items-center space-x-2 mb-3">
                  <Target className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Popular Searches</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">Explore common job paths our members search for:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {[
                    'Customer Service Representative',
                    'Remote Customer Support',
                    'Administrative Assistant',
                    'Warehouse Associate',
                    'Call Center Representative',
                    'Receptionist',
                    'Social Services / Case Manager',
                    'Medical Office Assistant',
                    'Retail Associate',
                    'Data Entry Specialist',
                    'Home Health Aide',
                    'Office Clerk'
                  ].map((job, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded-full text-center border border-gray-200"
                    >
                      {job}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Section - Sample Job Results */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Briefcase className="w-5 h-5 text-green-600" />
                  <span>Sample Job Results</span>
                </h3>
                <div className="space-y-4">
                  {/* Job Card 1 */}
                  <div className="p-5 border border-gray-200 rounded-lg hover:border-green-300 transition-colors bg-gradient-to-br from-white to-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Remote Customer Service Representative</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-600">
                          <span className="font-medium">BrightPath Solutions</span>
                          <span>•</span>
                          <span>Atlanta, GA (Remote)</span>
                        </div>
                        <div className="mt-1 text-sm font-semibold text-green-600">$18–$22 per hour</div>
                      </div>
                      <div className="px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg cursor-default">
                        View Job
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Assist customers with account questions and provide friendly support from home.
                    </p>
                  </div>

                  {/* Job Card 2 */}
                  <div className="p-5 border border-gray-200 rounded-lg hover:border-green-300 transition-colors bg-gradient-to-br from-white to-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Administrative Assistant</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-600">
                          <span className="font-medium">Greenway Community Health</span>
                          <span>•</span>
                          <span>Savannah, GA</span>
                        </div>
                      </div>
                      <div className="px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg cursor-default">
                        View Job
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Support office operations, schedule appointments, and handle basic clerical tasks.
                    </p>
                  </div>

                  {/* Job Card 3 */}
                  <div className="p-5 border border-gray-200 rounded-lg hover:border-green-300 transition-colors bg-gradient-to-br from-white to-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-1">Warehouse Associate</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-600">
                          <span className="font-medium">Metro Logistics</span>
                          <span>•</span>
                          <span>McDonough, GA</span>
                        </div>
                      </div>
                      <div className="px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg cursor-default">
                        View Job
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Help with picking, packing, and shipping orders in a fast-paced warehouse environment.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-20 bg-gradient-to-br from-sky-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Simple. Fast. Effective.</h2>
            <p className="text-lg text-gray-600">Get started in minutes</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-xl shadow-sky-500/30">
                1
              </div>
              <h3 className="text-2xl font-semibold mb-3">Create or Upload</h3>
              <p className="text-gray-600 leading-relaxed">
                Start from scratch with our AI builder or upload your existing resume to modernize it instantly
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-sky-500 to-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-xl shadow-orange-500/30">
                2
              </div>
              <h3 className="text-2xl font-semibold mb-3">Customize & Optimize</h3>
              <p className="text-gray-600 leading-relaxed">
                Use AI suggestions to improve content, tailor to specific jobs, and boost your ATS score
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6 shadow-xl shadow-orange-500/30">
                3
              </div>
              <h3 className="text-2xl font-semibold mb-3">Apply with Confidence</h3>
              <p className="text-gray-600 leading-relaxed">
                Download your resume, generate cover letters, and track applications all in one place
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-gradient-to-r from-sky-600 to-orange-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">Ready to Take the Next Step?</h2>
          <p className="text-xl mb-10 opacity-90">
            Join thousands of jobseekers who&apos;ve already transformed their careers with Career Support
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-btn"
            className="text-lg px-10 py-6 bg-white text-sky-600 hover:bg-gray-100 shadow-2xl"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Inline Content Ad - Page Specific */}
      <AdRenderer zone="inline_content" pagePath="/" trigger="on_scroll" className="max-w-4xl mx-auto my-8 px-6" />

      {/* Footer */}
      <footer className="px-6 py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">Career Support</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                AI-powered career tools to help you land your dream job faster.
              </p>
            </div>
            
            {/* Features */}
            <div>
              <h4 className="font-semibold mb-4 text-lg">Features</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/templates" className="hover:text-orange-400 transition-colors">Resume Builder</Link></li>
                <li><Link to="/jobs" className="hover:text-orange-400 transition-colors">Job Search</Link></li>
                <li><Link to="/interview-prep" className="hover:text-orange-400 transition-colors">Interview Prep</Link></li>
                <li><Link to="/salary-analyzer" className="hover:text-orange-400 transition-colors">Salary Analyzer</Link></li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="font-semibold mb-4 text-lg">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link to="/careers" className="hover:text-orange-400 transition-colors flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Careers - We&apos;re Hiring!
                  </Link>
                </li>
                <li><Link to="/partnerships" className="hover:text-orange-400 transition-colors">Become a Partner</Link></li>
                <li><Link to="/resources" className="hover:text-orange-400 transition-colors">Career Resources</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400 transition-colors">Contact Us</Link></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4 text-lg">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-orange-400 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-orange-400 transition-colors">Terms of Service</Link></li>
                <li><Link to="/help" className="hover:text-orange-400 transition-colors">Help Center</Link></li>
                <li><Link to="/faq" className="hover:text-orange-400 transition-colors">FAQ</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Bottom bar */}
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">&copy; 2025 Career Support. All rights reserved.</p>
            <div className="flex items-center gap-6 text-gray-400 text-sm">
              <a href="mailto:support@careersupport.io" className="hover:text-orange-400 transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" />
                support@careersupport.io
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
